import React from "react";
import { ArrowLeftRight, X, Scale, Briefcase, AlertCircle, Users } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import SelfArt from "./SelfArt";
import type { AlternateSelf } from "@/lib/infiniteSelves";
import { compareSelves } from "@/lib/infiniteSelves";
export default function CompareView(props: {
  a: AlternateSelf;
  b: AlternateSelf;
  onSwap: () => void;
  onClose: () => void;
}): JSX.Element {
  const {
    a,
    b,
    onSwap,
    onClose
  } = props;

  // Compute rich comparison via library function compareSelves
  const comparison = compareSelves(a, b);
  return <div className="ql-glass-strong relative overflow-hidden rounded-2xl border border-primary/40 p-4 sm:p-6 md:p-9 shadow-2xl space-y-6 sm:space-y-8 w-full max-w-full">
      {/* Top Header Horizon */}
      <div className="absolute top-0 left-0 right-0 h-1.5" style={{
      background: `linear-gradient(90deg, hsl(${a.visual.hue} 100% 50%), hsl(${b.visual.hue} 100% 50%))`
    }} />

      {/* Title & Control Actions */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-b border-border/60 pb-5">
        <div className="space-y-1 min-w-0">
          <div className="flex items-center gap-2">
            <Scale className="size-4 text-primary shrink-0" />
            <span className="text-xs font-mono uppercase tracking-widest text-primary font-bold">
              Parallel Selves Comparison
            </span>
          </div>
          <h2 className="font-serif text-xl sm:text-2xl md:text-3xl font-bold tracking-tight text-foreground break-words">
            {a.name} <span className="text-muted-foreground font-light">vs</span> {b.name}
          </h2>
        </div>

        <div className="flex flex-wrap items-center gap-2.5 self-stretch sm:self-auto shrink-0 w-full sm:w-auto min-w-0">
          <button type="button" onClick={onSwap} className="ql-btn-ghost min-h-[44px] text-xs flex-1 sm:flex-initial justify-center min-w-0 px-3" aria-label="Swap sides in comparison">
            <ArrowLeftRight className="size-3.5 mr-1.5 shrink-0" />
            <span className="truncate">Swap Sides</span>
          </button>

          <button type="button" onClick={onClose} className="min-h-[44px] px-4 rounded-full border border-border bg-muted/40 text-foreground hover:bg-accent text-xs font-mono transition-colors cursor-pointer flex-1 sm:flex-initial justify-center flex items-center min-w-0" aria-label="Close comparison view">
            <X className="size-4 mr-1.5 inline shrink-0" />
            <span className="truncate">Close</span>
          </button>
        </div>
      </div>

      {/* Side-by-Side Identity Cards (stacked on mobile, 2 cols on md+) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch">
        {/* Specimen A Card */}
        <div className="ql-card p-5 space-y-4 flex flex-col justify-between">
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-2">
              <Badge variant="outline" className="text-[10px] font-mono font-bold uppercase tracking-wider text-primary border-primary/40 bg-primary/10">
                {a.scenarioLabel}
              </Badge>
              <span className="text-[10px] font-mono text-muted-foreground">
                {a.timelineId}
              </span>
            </div>

            <SelfArt self={a} variant="card" />

            <div className="space-y-1 pt-1">
              <h3 className="font-serif text-xl font-bold text-foreground">
                {a.name}
              </h3>
              <p className="text-xs font-medium text-foreground/80 italic">
                {a.title}
              </p>
              <p className="text-xs text-primary font-mono">{a.occupation}</p>
            </div>
          </div>

          <div className="p-3 rounded-lg bg-muted/40 border border-border/60 text-xs space-y-1">
            <span className="text-[10px] font-mono uppercase text-muted-foreground block">
              Cosmos: {a.universe.name} ({a.universe.code})
            </span>
            <p className="text-muted-foreground italic line-clamp-2">
              "{a.tagline}"
            </p>
          </div>
        </div>

        {/* Specimen B Card */}
        <div className="ql-card p-5 space-y-4 flex flex-col justify-between">
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-2">
              <Badge variant="outline" className="text-[10px] font-mono font-bold uppercase tracking-wider text-chart-3 border-chart-3/40 bg-chart-3/10">
                {b.scenarioLabel}
              </Badge>
              <span className="text-[10px] font-mono text-muted-foreground">
                {b.timelineId}
              </span>
            </div>

            <SelfArt self={b} variant="card" />

            <div className="space-y-1 pt-1">
              <h3 className="font-serif text-xl font-bold text-foreground">
                {b.name}
              </h3>
              <p className="text-xs font-medium text-foreground/80 italic">
                {b.title}
              </p>
              <p className="text-xs text-chart-3 font-mono">{b.occupation}</p>
            </div>
          </div>

          <div className="p-3 rounded-lg bg-muted/40 border border-border/60 text-xs space-y-1">
            <span className="text-[10px] font-mono uppercase text-muted-foreground block">
              Cosmos: {b.universe.name} ({b.universe.code})
            </span>
            <p className="text-muted-foreground italic line-clamp-2">
              "{b.tagline}"
            </p>
          </div>
        </div>
      </div>

      {/* Axis Comparison Table (5 rows, AXIS_ORDER) */}
      <div className="space-y-4 border-t border-border/60 pt-6">
        <div className="flex items-center justify-between">
          <h3 className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
            Dimensional Alignment Matrix
          </h3>
          <span className="text-[10px] font-mono text-primary font-semibold">
            5 DIVERGENCE VECTORS
          </span>
        </div>

        <div className="space-y-3">
          {(comparison?.axes ?? [{
          axis: "curiosity",
          label: "Intellectual Curiosity",
          a: a.axes.curiosity ?? 50,
          b: b.axes.curiosity ?? 50,
          delta: (a.axes.curiosity ?? 50) - (b.axes.curiosity ?? 50),
          text: ""
        }, {
          axis: "connection",
          label: "Interpersonal Connection",
          a: a.axes.connection ?? 50,
          b: b.axes.connection ?? 50,
          delta: (a.axes.connection ?? 50) - (b.axes.connection ?? 50),
          text: ""
        }, {
          axis: "courage",
          label: "Risk & Courage",
          a: a.axes.courage ?? 50,
          b: b.axes.courage ?? 50,
          delta: (a.axes.courage ?? 50) - (b.axes.courage ?? 50),
          text: ""
        }, {
          axis: "stability",
          label: "Order & Stability",
          a: a.axes.stability ?? 50,
          b: b.axes.stability ?? 50,
          delta: (a.axes.stability ?? 50) - (b.axes.stability ?? 50),
          text: ""
        }, {
          axis: "influence",
          label: "Systemic Influence",
          a: a.axes.influence ?? 50,
          b: b.axes.influence ?? 50,
          delta: (a.axes.influence ?? 50) - (b.axes.influence ?? 50),
          text: ""
        }]).map(row => {
          const deltaSign = row.delta > 0 ? `+${row.delta}` : `${row.delta}`;
          return <div key={row.axis} className="p-3.5 sm:p-4 rounded-xl bg-card border border-border space-y-3 min-w-0">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs min-w-0">
                  <span className="font-semibold text-foreground font-serif text-sm break-words min-w-0">
                    {row.label}
                  </span>
                  <div className="flex flex-wrap items-center gap-2 sm:gap-3 font-mono text-xs min-w-0">
                    <span className="text-primary font-bold break-words min-w-0">
                      {a.name.split(" ")[0]}: {row.a}
                    </span>
                    <span className="text-muted-foreground">vs</span>
                    <span className="text-chart-3 font-bold break-words min-w-0">
                      {b.name.split(" ")[0]}: {row.b}
                    </span>
                    <Badge variant="outline" className={`text-[10px] px-2 py-0 border-border font-mono shrink-0 ${Math.abs(row.delta) >= 15 ? "text-primary border-primary/40 bg-primary/10 shadow-[0_0_8px_var(--glow-primary)]" : "text-muted-foreground"}`}>
                      Δ {deltaSign}
                    </Badge>
                  </div>
                </div>

                {/* Comparative Dual Progress Bars with glowing tracks */}
                <div className="grid grid-cols-2 gap-3 items-center min-w-0">
                  <div className="space-y-1 min-w-0">
                    <div className="h-2 rounded-full bg-muted/80 overflow-hidden border border-border/40">
                      <div className="h-full rounded-full transition-all duration-300 shadow-[0_0_8px_var(--glow-primary)]" style={{
                    width: `${Math.max(4, Math.min(100, row.a))}%`,
                    backgroundColor: `hsl(${a.visual.hue} 100% 50%)`
                  }} />
                    </div>
                  </div>
                  <div className="space-y-1 min-w-0">
                    <div className="h-2 rounded-full bg-muted/80 overflow-hidden border border-border/40">
                      <div className="h-full rounded-full transition-all duration-300 shadow-[0_0_8px_var(--glow-laser)]" style={{
                    width: `${Math.max(4, Math.min(100, row.b))}%`,
                    backgroundColor: `hsl(${b.visual.hue} 100% 50%)`
                  }} />
                    </div>
                  </div>
                </div>

                {/* Descriptive sentence from library */}
                {row.text ? <p className="text-xs text-muted-foreground font-sans leading-relaxed pt-1 break-words">
                    {row.text}
                  </p> : null}
              </div>;
        })}
        </div>
      </div>

      {/* Narrative Synthesis Blocks */}
      {comparison && <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-border/60 pt-6">
            {/* Synthesis Summary */}
            {comparison.summary && <div className="p-4 rounded-xl bg-card border border-border space-y-2">
                <span className="text-[10px] font-mono uppercase tracking-wider text-primary font-bold block">
                  Comparative Synthesis
                </span>
                <p className="text-xs sm:text-sm text-foreground/90 leading-relaxed font-sans">
                  {comparison.summary}
                </p>
              </div>}

            {/* Shared Ground */}
            {comparison.sharedGround && <div className="p-4 rounded-xl bg-card border border-border space-y-2">
                <span className="text-[10px] font-mono uppercase tracking-wider text-foreground/80 font-bold block">
                  Shared Resonance & Common Ground
                </span>
                <p className="text-xs sm:text-sm text-foreground/90 leading-relaxed font-sans">
                  {comparison.sharedGround}
                </p>
              </div>}

            {/* Biggest Divergence */}
            {comparison.biggestDivergence && <div className="p-4 rounded-xl bg-card border border-border space-y-2">
                <span className="text-[10px] font-mono uppercase tracking-wider text-chart-3 font-bold block">
                  Primary Point of Divergence
                </span>
                <p className="text-xs sm:text-sm text-foreground/90 leading-relaxed font-sans">
                  {comparison.biggestDivergence}
                </p>
              </div>}

            {/* Universe Contrast */}
            {comparison.universeContrast && <div className="p-4 rounded-xl bg-card border border-border space-y-2">
                <span className="text-[10px] font-mono uppercase tracking-wider text-primary font-bold block">
                  Cosmological Contrast
                </span>
                <p className="text-xs sm:text-sm text-foreground/90 leading-relaxed font-sans">
                  {comparison.universeContrast}
                </p>
              </div>}

            {/* Life Choices Contrast spanning full width */}
            {comparison.choiceContrast && <div className="md:col-span-2 p-4 rounded-xl bg-card border border-border space-y-2">
                <span className="text-[10px] font-mono uppercase tracking-wider text-foreground/80 font-bold block">
                  Divergent Life Choices
                </span>
                <p className="text-xs sm:text-sm text-foreground/90 leading-relaxed font-sans">
                  {comparison.choiceContrast}
                </p>
              </div>}
          </div>

          {/* Persona Dossier Comparison: Occupation, Dilemma & Relationships */}
          {(a.occupation || b.occupation || a.dossier?.dilemma || b.dossier?.dilemma || a.dossier?.relationships && a.dossier.relationships.length > 0 || b.dossier?.relationships && b.dossier.relationships.length > 0) && <div className="space-y-4 border-t border-border/60 pt-6">
              <div className="flex items-center justify-between">
                <h3 className="text-xs font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                  <Scale className="size-3.5 text-primary shrink-0" />
                  <span>Dossier & Reality Comparison</span>
                </h3>
                <span className="text-[10px] font-mono text-primary font-semibold">
                  ROLES · DILEMMAS · BONDS
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
                {/* Specimen A Dossier Column */}
                <div className="p-4 sm:p-5 rounded-xl bg-card border border-primary/30 space-y-4 shadow-sm min-w-0">
                  <div className="flex items-center justify-between border-b border-border/60 pb-2.5 gap-2 min-w-0">
                    <span className="font-serif font-bold text-foreground text-sm flex items-center gap-2 min-w-0">
                      <span className="size-2 rounded-full bg-primary shrink-0" />
                      <span className="truncate break-words">{a.name}</span>
                    </span>
                    <Badge variant="outline" className="text-[10px] font-mono text-primary border-primary/40 shrink-0">
                      {a.scenarioLabel}
                    </Badge>
                  </div>

                  {/* Occupation */}
                  {a.occupation && <div className="space-y-1 min-w-0">
                      <span className="text-[10px] font-mono uppercase text-muted-foreground flex items-center gap-1.5 font-semibold">
                        <Briefcase className="size-3 text-primary shrink-0" />
                        <span>Occupation & Role</span>
                      </span>
                      <p className="text-xs sm:text-sm text-foreground font-medium pl-4 break-words">
                        {a.occupation}
                      </p>
                    </div>}

                  {/* Defining Dilemma */}
                  {a.dossier?.dilemma && <div className="space-y-1.5 p-3 rounded-lg bg-muted/40 border border-border/60 min-w-0">
                      <span className="text-[10px] font-mono uppercase text-chart-3 font-semibold flex items-center gap-1.5">
                        <AlertCircle className="size-3 text-chart-3 shrink-0" />
                        <span>Defining Dilemma</span>
                      </span>
                      <p className="text-xs text-foreground/90 leading-relaxed font-sans break-words">
                        {a.dossier.dilemma}
                      </p>
                    </div>}

                  {/* Key Relationships */}
                  {a.dossier?.relationships && a.dossier.relationships.length > 0 && <div className="space-y-2 min-w-0">
                      <span className="text-[10px] font-mono uppercase text-muted-foreground flex items-center gap-1.5 font-semibold">
                        <Users className="size-3 text-primary shrink-0" />
                        <span>Key Relationships</span>
                      </span>
                      <div className="space-y-2 pl-2 border-l border-border/60 min-w-0">
                        {a.dossier.relationships.map((rel, idx) => <div key={idx} className="text-xs space-y-0.5 min-w-0">
                            <div className="flex items-baseline gap-1.5 flex-wrap min-w-0">
                              <span className="font-semibold text-foreground break-words">{rel.who}</span>
                              <span className="text-[10px] font-mono text-primary uppercase shrink-0">({rel.role})</span>
                            </div>
                            <p className="text-[11px] text-muted-foreground leading-relaxed font-sans break-words">
                              {rel.bond}
                            </p>
                          </div>)}
                      </div>
                    </div>}
                </div>

                {/* Specimen B Dossier Column */}
                <div className="p-4 sm:p-5 rounded-xl bg-card border border-chart-3/30 space-y-4 shadow-sm min-w-0">
                  <div className="flex items-center justify-between border-b border-border/60 pb-2.5 gap-2 min-w-0">
                    <span className="font-serif font-bold text-foreground text-sm flex items-center gap-2 min-w-0">
                      <span className="size-2 rounded-full bg-chart-3 shrink-0" />
                      <span className="truncate break-words">{b.name}</span>
                    </span>
                    <Badge variant="outline" className="text-[10px] font-mono text-chart-3 border-chart-3/40 shrink-0">
                      {b.scenarioLabel}
                    </Badge>
                  </div>

                  {/* Occupation */}
                  {b.occupation && <div className="space-y-1 min-w-0">
                      <span className="text-[10px] font-mono uppercase text-muted-foreground flex items-center gap-1.5 font-semibold">
                        <Briefcase className="size-3 text-chart-3 shrink-0" />
                        <span>Occupation & Role</span>
                      </span>
                      <p className="text-xs sm:text-sm text-foreground font-medium pl-4 break-words">
                        {b.occupation}
                      </p>
                    </div>}

                  {/* Defining Dilemma */}
                  {b.dossier?.dilemma && <div className="space-y-1.5 p-3 rounded-lg bg-muted/40 border border-border/60 min-w-0">
                      <span className="text-[10px] font-mono uppercase text-chart-3 font-semibold flex items-center gap-1.5">
                        <AlertCircle className="size-3 text-chart-3 shrink-0" />
                        <span>Defining Dilemma</span>
                      </span>
                      <p className="text-xs text-foreground/90 leading-relaxed font-sans break-words">
                        {b.dossier.dilemma}
                      </p>
                    </div>}

                  {/* Key Relationships */}
                  {b.dossier?.relationships && b.dossier.relationships.length > 0 && <div className="space-y-2 min-w-0">
                      <span className="text-[10px] font-mono uppercase text-muted-foreground flex items-center gap-1.5 font-semibold">
                        <Users className="size-3 text-chart-3 shrink-0" />
                        <span>Key Relationships</span>
                      </span>
                      <div className="space-y-2 pl-2 border-l border-border/60 min-w-0">
                        {b.dossier.relationships.map((rel, idx) => <div key={idx} className="text-xs space-y-0.5 min-w-0">
                            <div className="flex items-baseline gap-1.5 flex-wrap min-w-0">
                              <span className="font-semibold text-foreground break-words">{rel.who}</span>
                              <span className="text-[10px] font-mono text-chart-3 uppercase shrink-0">({rel.role})</span>
                            </div>
                            <p className="text-[11px] text-muted-foreground leading-relaxed font-sans break-words">
                              {rel.bond}
                            </p>
                          </div>)}
                      </div>
                    </div>}
                </div>
              </div>
            </div>}
        </>}
    </div>;
}